import { ColumnType } from "@/queries/smart-tables/types";
import {
  type CompositionSourceDefinition,
  getCompositionSourceDefinitions,
  resolveColumnType,
  resolveCompositionColumnDisplayTitle,
} from "@/routes/smart-table/utils/compositionColumns";

export interface SmartTableBaseColumnLike {
  id: string;
  title: string;
  type?: ColumnType | string;
  config?: Record<string, unknown> | null;
}

export interface BaseLeafColumnContext<
  TColumn extends SmartTableBaseColumnLike,
  TValueGetterParams = unknown,
> {
  column: TColumn;
  colId: string;
  field?: string;
  headerName: string;
  columnType?: ColumnType;
  config?: Record<string, unknown> | null;
  valueGetter?: (params: TValueGetterParams) => unknown;
  columnGroupShow?: "open" | "closed";
  parentColumnId?: string;
  compositionSource?: string;
}

export interface BaseGroupColumnContext<TColumn extends SmartTableBaseColumnLike, TLeaf> {
  column: TColumn;
  groupId: string;
  headerName: string;
  columnType?: ColumnType;
  config?: Record<string, unknown> | null;
  children: TLeaf[];
}

export interface BuildSmartTableBaseColumnDefsOptions<
  TColumn extends SmartTableBaseColumnLike,
  TLeaf,
  TGroup,
  TValueGetterParams = unknown,
> {
  createLeafColumnDef: (context: BaseLeafColumnContext<TColumn, TValueGetterParams>) => TLeaf;
  createGroupColumnDef?: (context: BaseGroupColumnContext<TColumn, TLeaf>) => TGroup;
  getCompositionDefinitions?: (column: TColumn) => CompositionSourceDefinition[];
  getColumnType?: (column: TColumn, isMultiSourceComposition: boolean) => ColumnType | undefined;
  getRowFromValueGetterParams?: (params: TValueGetterParams) => Record<string, unknown> | undefined;
  resolveCompositionSourceValue?: (
    params: TValueGetterParams,
    sourceDefinition: CompositionSourceDefinition,
  ) => unknown;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const defaultGetColumnType = <TColumn extends SmartTableBaseColumnLike>(
  column: TColumn,
  isMultiSourceComposition: boolean,
): ColumnType | undefined =>
  resolveColumnType(column.type, isMultiSourceComposition ? ColumnType.COMPOSITION : undefined);

const defaultGetRowFromValueGetterParams = <TValueGetterParams>(
  params: TValueGetterParams,
): Record<string, unknown> | undefined => {
  if (!isRecord(params)) {
    return undefined;
  }

  const possibleData = params.data;
  return isRecord(possibleData) ? possibleData : undefined;
};

const makeDefaultResolveCompositionSourceValue =
  <TValueGetterParams>(
    getRowFromParams: (params: TValueGetterParams) => Record<string, unknown> | undefined,
  ) =>
  (params: TValueGetterParams, sourceDefinition: CompositionSourceDefinition): unknown => {
    const row = getRowFromParams(params);
    if (!row) {
      return null;
    }

    return Object.prototype.hasOwnProperty.call(row, sourceDefinition.childField)
      ? row[sourceDefinition.childField]
      : null;
  };

export const buildSmartTableBaseColumnDef = <
  TColumn extends SmartTableBaseColumnLike,
  TLeaf,
  TGroup,
  TValueGetterParams = unknown,
>(
  column: TColumn,
  options: BuildSmartTableBaseColumnDefsOptions<TColumn, TLeaf, TGroup, TValueGetterParams>,
): TLeaf | TGroup => {
  const getDefinitions = options.getCompositionDefinitions ?? getCompositionSourceDefinitions;
  const sourceDefinitions = getDefinitions(column);
  const isMultiSourceComposition = sourceDefinitions.length > 1;

  const getColumnType = options.getColumnType ?? defaultGetColumnType;
  const columnType = getColumnType(column, isMultiSourceComposition);

  if (!isMultiSourceComposition) {
    const headerName = resolveCompositionColumnDisplayTitle(column, column.title) ?? column.title;

    return options.createLeafColumnDef({
      column,
      colId: column.id,
      field: column.id,
      headerName,
      columnType,
      config: column.config ?? null,
    });
  }

  const getRowFromParams =
    options.getRowFromValueGetterParams ?? defaultGetRowFromValueGetterParams<TValueGetterParams>;

  const resolveCompositionSourceValue =
    options.resolveCompositionSourceValue ??
    makeDefaultResolveCompositionSourceValue(getRowFromParams);

  const children = sourceDefinitions.map((sourceDefinition, index) =>
    options.createLeafColumnDef({
      column,
      colId: sourceDefinition.childField,
      field: sourceDefinition.childField,
      headerName: sourceDefinition.title,
      columnType: sourceDefinition.type,
      config: column.config ?? null,
      valueGetter: params => resolveCompositionSourceValue(params, sourceDefinition),
      columnGroupShow: index === 0 ? undefined : "open",
      parentColumnId: column.id,
      compositionSource: sourceDefinition.source,
    }),
  );

  if (!options.createGroupColumnDef) {
    throw new Error("createGroupColumnDef is required for multi-source composition columns");
  }

  return options.createGroupColumnDef({
    column,
    groupId: column.id,
    headerName: column.title,
    columnType,
    config: column.config ?? null,
    children,
  });
};

export const buildSmartTableBaseColumnDefs = <
  TColumn extends SmartTableBaseColumnLike,
  TLeaf,
  TGroup,
  TValueGetterParams = unknown,
>(
  columns: readonly TColumn[],
  options: BuildSmartTableBaseColumnDefsOptions<TColumn, TLeaf, TGroup, TValueGetterParams>,
): (TLeaf | TGroup)[] =>
  columns.map(column =>
    buildSmartTableBaseColumnDef<TColumn, TLeaf, TGroup, TValueGetterParams>(column, options),
  );
